import { Injectable } from '@angular/core';
import {Observable} from 'rxjs';
import { Reducer, Store } from '@ngrx/store';

import {Broker} from '@c4/core';
import {ProgrammingUtilsService} from './utils.service';

const STORE_NAME: string = 'GUIDED_BUTTON:';
const SET_CATEGORIES: string = STORE_NAME + 'SET_CATEGORIES';
const SELECT_CATEGORY: string = STORE_NAME + 'SELECT_CATEGORY';
const SET_ERROR: string = STORE_NAME + 'SET_ERROR';
const SET_DEVICES: string = STORE_NAME + 'SET_DEVICES';
const SELECT_DEVICE: string = STORE_NAME + 'SELECT_DEVICE';
const SELECT_BUTTON: string = STORE_NAME + 'SELECT_BUTTON';
const SELECT_INTERACTION: string = STORE_NAME + 'SELECT_INTERACTION';
const SET_EVENTS: string = STORE_NAME + 'SET_EVENTS';
const SELECT_EVENT: string = STORE_NAME + 'SELECT_EVENT';
const CLEAR_ALL: string = STORE_NAME + 'CLEAR_ALL';
const CLEAR_CATEGORY: string = STORE_NAME + 'CLEAR_CATEGORY';
const CLEAR_DEVICE: string = STORE_NAME + 'CLEAR_DEVICE';
const CLEAR_BUTTON: string = STORE_NAME + 'CLEAR_BUTTON';
const CLEAR_INTERACTION: string = STORE_NAME + 'CLEAR_INTERACTION';
const CLEAR_EVENT: string = STORE_NAME + 'CLEAR_EVENT';

export interface GuidedButtonContext {
  categories: Array<any>;
  devices: Array<any>;
  error: any;
  category: any;
  device: any;
  button: string;
  interaction: string;
  events: Array<any>;
  event: any;
}

const HAVE_BUTTONS: Array<string> = [
  'uidevice',
  'keypad_proxy',
  'lights'
];

const CLEAR_BUTTON_STATE: any = {
  button: undefined
}

const CLEAR_INTERACTION_STATE: any = {
  interaction: undefined
}

const CLEAR_EVENT_STATE: any = {
  event: undefined
}

const INITIAL_STATE: any = {
  categories: [],
  devices: [],
  events: [],
  error: undefined,
  category: undefined,
  device: undefined,
  button: undefined,
  interaction: undefined,
  event: undefined
};

const CLEAR_CATEGORY_STATE: any = {
  devices: [],
  events: [],
  category: undefined,
  device: undefined,
  button: undefined,
  interaction: undefined,
  event: undefined
};

const CLEAR_DEVICE_STATE: any = {
  device: undefined,
  button: undefined,
  interaction: undefined,
  event: undefined
};

export function guidedButtonReducer(state: GuidedButtonContext = INITIAL_STATE, {type, payload}) {
  switch (type) {
    case SET_CATEGORIES:
      return Object.assign({}, state, { categories: payload });
    case SET_DEVICES:
      return Object.assign({}, state, { devices: payload });
    case SET_ERROR:
      return Object.assign({}, state, { error: payload });
    case SELECT_CATEGORY:
      return Object.assign({}, state, { category: payload });
    case SELECT_DEVICE:
      return Object.assign({}, state, { device: payload });
    case SELECT_BUTTON:
      return Object.assign({}, state, { button: payload });
    case SELECT_INTERACTION:
      return Object.assign({}, state, { interaction: payload });
    case SET_EVENTS:
      return Object.assign({}, state, { events: payload });
    case SELECT_EVENT:
      return Object.assign({}, state, { event: payload });
    case CLEAR_ALL:
      return Object.assign({}, state, INITIAL_STATE);
    case CLEAR_CATEGORY:
      return Object.assign({}, state, CLEAR_CATEGORY_STATE);
    case CLEAR_DEVICE:
      return Object.assign({}, state, CLEAR_DEVICE_STATE);
    case CLEAR_BUTTON:
      return Object.assign({}, state, CLEAR_BUTTON_STATE);
    case CLEAR_INTERACTION:
      return Object.assign({}, state, CLEAR_INTERACTION_STATE);
    case CLEAR_EVENT:
      return Object.assign({}, state, CLEAR_EVENT_STATE);
    default: return state;
  }
};

@Injectable()
export class GuidedButtonService {

  guidedButton: Observable<any>;

  constructor(
    private store: Store<any>,
    private broker: Broker,
    private utils: ProgrammingUtilsService
  ) {
    this.guidedButton = this.store.select('guidedButton');
  }

  selectCategory(cat: any) {
    this.store.dispatch({ type: SELECT_CATEGORY, payload: cat });

    this.broker.call({
      path: '/api/v1/categories/' + cat.value
    }).then(
      res => {
        let devices = this.utils.deriveDevicesFromItems(res);
        this.store.dispatch({ type: SET_DEVICES, payload: devices });
      },
      err => {
        this.store.dispatch({ type: SET_ERROR, payload: { message: 'Unable to get devices for category: ' + cat.label } });
      });
  }

  selectDevice(dev: any) {
    this.store.dispatch({ type: SELECT_DEVICE, payload: dev });

    let id = (dev.defaultProxy) ? dev.defaultProxy.id : dev.id;
    this.broker.call({
      path: '/api/v1/items/' + id + '/events?allevents=true'
    }).then(
      (events: Array<any>) => {
        this.store.dispatch({ type: SET_EVENTS, payload: events });
      },
      err => {
        console.error('guidedbtnsvc: error getting events for device', err);
      });
  }

  selectButton(btn: any) {
    this.store.dispatch({ type: SELECT_BUTTON, payload: btn });
    this.findEvent();
  }

  selectInteraction(inter: any) {
    this.store.dispatch({ type: SELECT_INTERACTION, payload: inter });
    this.findEvent();
  }

  clear() {
    this.store.dispatch({ type: CLEAR_ALL });
  }

  clearCategory() {
    this.store.dispatch({ type: CLEAR_CATEGORY });
  }

  clearDevice() {
    this.store.dispatch({ type: CLEAR_DEVICE });
  }

  clearButtonInteraction() {
    this.store.dispatch({ type: CLEAR_BUTTON });
    this.store.dispatch({ type: CLEAR_INTERACTION });
  }

  clearEvent() {
    this.store.dispatch({ type: CLEAR_EVENT });
  }

  // TODO re-evaluate the need for these two getters
  get selectedCategory(): any {
    return this.getValue(s => s.guidedButton).category;
  }

  get selectedEvent(): any {
    return this.getValue(s => s.guidedButton).event;
  }

  private hasButtons(cat: any): boolean {
    return (HAVE_BUTTONS.indexOf(cat) >= 0);
  }

  private getValue = (callback: (state: any) => any): any => { };

  private findEvent() {
    let found = null;

    this.store.select(state => state.guidedButton).take(1).subscribe(
      guided => {
        if (guided.interaction && guided.button) {
          if (guided.events) {
            let inter = guided.interaction.toLowerCase();
            let button = guided.button.toLowerCase();

            for (var i = 0; i < guided.events.length; i++) {
              var event = guided.events[i];
              let disp = event.display.toLowerCase();
              if (disp.indexOf(button) >= 0 && disp.indexOf(inter) >= 0) {
                found = event;
                break;
              }
            }
          }
        }
      });

    this.store.dispatch({ type: SELECT_EVENT, payload: found });
  }
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/common/services/guided-button.service.ts